import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Building2,
  CheckCircle2,
  ChevronDown,
  Clock,
  Edit3,
  ExternalLink,
  Globe,
  Lock,
  MapPin,
  MousePointer2,
  Phone,
  RefreshCw,
  Search,
  Sparkles,
  Store,
  Target,
  X,
} from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import {
  googleBusinessApi,
  type GbpAiInsight,
  type GbpBusinessInfo,
  type GbpDashboard,
  type GbpHours,
  type GbpLocation,
  type GbpPerformance,
  type GbpSettings,
} from "../../api/googleBusiness";
import { ApiError } from "../../api/client";
import { formatJstDateTime } from "../../utils/dateTime";

type TrendMetric = "searches" | "calls" | "websiteClicks" | "directions";
type EditMode = "website" | "hours" | null;

type ActionDefinition = {
  title: string;
  description: string;
};

type InsightItem = {
  severity: "positive" | "warning" | "critical" | "neutral";
  title: string;
  description: string;
};

type InsightData = {
  summary: string;
  insights: InsightItem[];
  actions: Array<{ action_id: string; priority: "high" | "medium" | "low" }>;
};

const ACTIONS: Record<string, ActionDefinition> = {
  add_photos: { title: "写真を追加", description: "店舗・商品・外観の写真を増やし、来店前の不安を減らします。" },
  improve_description: { title: "説明文を改善", description: "強み・対象顧客・提供サービスを短く分かりやすく整理します。" },
  improve_website_cta: { title: "サイト導線を改善", description: "Webサイトへのクリック後に問い合わせ・予約へ進みやすくします。" },
  update_hours: { title: "営業時間を確認", description: "祝日や臨時営業を含め、来店前に迷わない状態にします。" },
};

const TREND_META: Record<TrendMetric, { label: string; icon: JSX.Element; color: string }> = {
  searches: { label: "検索数", icon: <Search size={18} />, color: "#2563eb" },
  calls: { label: "電話数", icon: <Phone size={18} />, color: "#16a34a" },
  websiteClicks: { label: "クリック数", icon: <MousePointer2 size={18} />, color: "#7c3aed" },
  directions: { label: "ルート検索数", icon: <MapPin size={18} />, color: "#ea580c" },
};

const PAGE_CACHE_TTL_MS = 30 * 60 * 1000;

type TimedCache<T> = {
  savedAt: number;
  value: T;
};

type GbpPageCache = {
  dashboard: GbpDashboard;
  businessInfo: GbpBusinessInfo;
  performance: GbpPerformance;
};

function readTimedCache<T>(key: string): T | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TimedCache<T>;
    if (!parsed || Date.now() - parsed.savedAt > PAGE_CACHE_TTL_MS) {
      window.sessionStorage.removeItem(key);
      return null;
    }
    return parsed.value;
  } catch {
    window.sessionStorage.removeItem(key);
    return null;
  }
}

function writeTimedCache<T>(key: string, value: T): void {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(key, JSON.stringify({ savedAt: Date.now(), value }));
}

function gbpPageCacheKey(locationId: string, period: number): string {
  return `gbp.page.${locationId}.${period}`;
}

function gbpInsightCacheKey(locationId: string, period: number): string {
  return `gbp.aiInsight.${locationId}.${period}`;
}

function fmtDate(v?: string | null) {
  return v ? formatJstDateTime(v) : "未同期";
}

function statusLabel(value?: string) {
  if (!value) return "不明";
  if (value === "OPEN") return "営業中";
  if (value === "CLOSED_TEMPORARILY") return "臨時休業";
  if (value === "CLOSED_PERMANENTLY") return "閉業";
  return value;
}

function percentChange(current: number, previous: number) {
  if (!previous) return current ? "+100%" : "0%";
  const value = ((current - previous) / previous) * 100;
  const rounded = Math.round(value * 10) / 10;
  return `${rounded > 0 ? "+" : ""}${rounded}%`;
}

function priorityLabel(priority?: string) {
  if (priority === "high") return "高";
  if (priority === "low") return "低";
  return "中";
}

function sumMetric(rows: GbpPerformance["daily"], metric: TrendMetric, start: number, end: number) {
  return rows.slice(start, end).reduce((total, row) => total + Number(row[metric] || 0), 0);
}

function formatShortDate(dateText?: string) {
  if (!dateText) return "";
  const [, month, day] = dateText.split("-");
  return month && day ? `${Number(month)}/${Number(day)}` : dateText;
}

function secondaryTrendMetric(metric: TrendMetric): TrendMetric {
  if (metric === "websiteClicks") return "directions";
  if (metric === "directions") return "websiteClicks";
  return "websiteClicks";
}

function StoreSwitcher({
  locations,
  currentLocationId,
  onChange,
}: {
  locations: GbpLocation[];
  currentLocationId: string | null;
  onChange: (id: string) => void;
}) {
  return (
    <label className="gbp-store-switcher gbp-store-switcher--compact">
      <span>店舗</span>
      <div className="gbp-select-wrap">
        <select value={currentLocationId || ""} onChange={(e) => onChange(e.target.value)} disabled={!locations.length}>
          {locations.map((loc) => <option key={loc.id} value={loc.id}>{loc.title}</option>)}
        </select>
        <ChevronDown size={16} />
      </div>
    </label>
  );
}

function normalizeAiInsight(value?: GbpAiInsight): InsightData | null {
  if (!value || typeof value.summary !== "string") return null;
  const blockedReviewClaim = /(レビュー|口コミ|クチコミ|評価).*(0件|0\.0|未表示|空白|障害|未連携|信頼指標|信頼構築)/;
  const insights = (value.insights || [])
    .filter((item) => item && typeof item.title === "string" && typeof item.description === "string")
    .filter((item) => !blockedReviewClaim.test(`${item.title} ${item.description}`))
    .slice(0, 3)
    .map((item) => ({
      severity: ["positive", "warning", "critical", "neutral"].includes(item.severity) ? item.severity : "neutral",
      title: item.title,
      description: item.description,
    }));
  const actions = (value.actions || [])
    .filter((action) => action && typeof action.action_id === "string")
    .slice(0, 4)
    .map((action) => ({
      action_id: action.action_id,
      priority: ["high", "medium", "low"].includes(action.priority) ? action.priority : "medium",
    }));
  if (!insights.length || !actions.length) return null;
  return { summary: value.summary, insights, actions };
}

function KpiCard({ metric, value, change, series }: { metric: TrendMetric; value: number; change: string; series: number[] }) {
  const meta = TREND_META[metric];
  return (
    <div className="gbp-kpi-card">
      <div className="gbp-kpi-icon" style={{ color: meta.color }}>{meta.icon}</div>
      <span>{meta.label}</span>
      <strong>{value.toLocaleString()}</strong>
      <small className={change.startsWith("-") ? "negative" : "positive"}>{change} / 前期間比</small>
      <MiniLine data={series} color={meta.color} />
    </div>
  );
}

function MiniLine({ data, color = "#2563eb" }: { data: number[]; color?: string }) {
  const points = data.length ? data : [0];
  const max = Math.max(...points, 1);
  const path = points.map((v, i) => {
    const x = (i / Math.max(points.length - 1, 1)) * 120;
    const y = 42 - (v / max) * 34;
    return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  return <svg viewBox="0 0 120 48" className="gbp-mini-line" aria-hidden="true"><path d={path} fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}

function TrendChart({ data, metric }: { data: GbpPerformance["daily"]; metric: TrendMetric }) {
  const meta = TREND_META[metric];
  const max = Math.max(...data.map((row) => Number(row[metric] || 0)), 1);
  const ticks = Array.from(new Set([max, Math.round(max / 2), 0])).filter((value) => Number.isFinite(value));
  const labelIndexes = data.length
    ? Array.from(new Set([0, Math.floor((data.length - 1) / 2), data.length - 1]))
    : [];
  return (
    <div className="gbp-trend-plot">
      <div className="gbp-trend-title">
        <span>{meta.label}</span>
        <strong>{data.reduce((total, row) => total + Number(row[metric] || 0), 0).toLocaleString()}</strong>
      </div>
      <div className="gbp-trend-chart-wrap">
        <div className="gbp-trend-y-axis">
          {ticks.map((tick) => <span key={tick}>{tick}</span>)}
        </div>
        <div className="gbp-trend-chart">
          {data.length ? data.map((row) => {
            const value = Number(row[metric] || 0);
            return <div key={row.date} className="gbp-trend-bar" style={{ height: `${8 + (value / max) * 82}px`, background: meta.color }} title={`${row.date}: ${value}`} />;
          }) : <div className="gbp-empty">パフォーマンス指標がありません。</div>}
        </div>
      </div>
      {labelIndexes.length > 0 && (
        <div className="gbp-trend-x-axis">
          <span />
          {labelIndexes.map((index) => <span key={index}>{formatShortDate(data[index]?.date)}</span>)}
        </div>
      )}
    </div>
  );
}

function BusinessField({
  icon,
  label,
  value,
  editable,
  onEdit,
  external,
}: {
  icon: JSX.Element;
  label: string;
  value: string;
  editable?: boolean;
  onEdit?: () => void;
  external?: boolean;
}) {
  // 2026-09-16: external link を値(value)の直後に inline 表示。
  // - 旧実装:カード右端に別 column の icon → 配置が壊れて超出しがち
  // - 新実装:label / value と同じ block 内に inline <a> を置き、blog と同じ
  //   blog-act / blog-act-external class を借りて 32×32 icon button 風にする
  // - URL が http(s):// 始まりでないなら → a を render しない(無効扱い)
  const externalUrl = value?.trim() || "";
  const externalHref = /^https?:\/\//i.test(externalUrl) ? externalUrl : "";
  return (
    <div className="gbp-business-field">
      <span className="gbp-business-field-icon">{icon}</span>
      <div>
        <span>
          {label}
          {external && externalHref ? (
            <a
              className="blog-act blog-act-external gbp-inline-external"
              href={externalHref}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${label}を新しいタブで開く`}
              title={externalHref}
            >
              <ExternalLink size={12} />
            </a>
          ) : null}
        </span>
        <strong>{value || "-"}</strong>
      </div>
      {editable ? (
        <button type="button" onClick={onEdit} aria-label={`${label}を編集`}><Edit3 size={14} /> 編集</button>
      ) : (
        <em><Lock size={12} /> Googleのみ</em>
      )}
    </div>
  );
}

function AiInsightPanel({ data }: { data: InsightData }) {
  return (
    <div className="gbp-ai-insight-panel">
      <p className="gbp-ai-summary">{data.summary}</p>
      <div className="gbp-ai-section">
        <span>主な気づき</span>
        {data.insights.map((item) => (
          <div className={`gbp-ai-insight gbp-ai-insight--${item.severity}`} key={item.title}>
            <i />
            <div><strong>{item.title}</strong><p>{item.description}</p></div>
          </div>
        ))}
      </div>
      <div className="gbp-ai-section">
        <span>おすすめアクション</span>
        <div className="gbp-ai-actions">
          {data.actions.map((action) => {
            const definition = ACTIONS[action.action_id] || { title: action.action_id, description: "この項目を確認してください。" };
            return (
              <div className={`gbp-ai-action gbp-ai-action--${action.priority}`} key={action.action_id}>
                <strong>{definition.title}</strong>
                <p>{definition.description}</p>
                <small>{priorityLabel(action.priority)}</small>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function buildInsight(dashboard: GbpDashboard | null, performance: GbpPerformance | null): InsightData {
  const totals = performance?.totals || dashboard?.metrics30d || { searches: 0, calls: 0, websiteClicks: 0, directions: 0 };
  const totalActions = totals.calls + totals.websiteClicks + totals.directions;
  const topMetric = Object.entries(totals).sort((a, b) => Number(b[1]) - Number(a[1]))[0]?.[0] as TrendMetric | undefined;
  const aiLines = performance?.aiAnalysis.text?.split("\n").filter(Boolean).slice(0, 3) || [];
  const summary = aiLines[0] || `直近30日間は検索 ${totals.searches.toLocaleString()}、電話 ${totals.calls.toLocaleString()}、Webサイトクリック ${totals.websiteClicks.toLocaleString()}、経路検索 ${totals.directions.toLocaleString()} です。Google上で見つけてもらう力と、問い合わせに進む導線を分けて確認できます。`;
  const insights: InsightItem[] = [
    {
      severity: totals.searches > totalActions ? "positive" : "neutral",
      title: "発見される導線を確認",
      description: topMetric ? `${TREND_META[topMetric].label} が最も大きい指標です。検索後に電話・Web・経路へ進んでいるかを見る必要があります。` : "表示可能な指標から流入と行動を確認します。",
    },
    {
      severity: totals.websiteClicks || totals.calls ? "positive" : "warning",
      title: "行動につながる指標",
      description: `電話・Webサイトクリック・経路検索の合計は ${totalActions.toLocaleString()} です。ここが少ない場合はプロフィールとWeb導線を優先します。`,
    },
    {
      severity: "neutral",
      title: "レビューは公式画面で確認",
      description: "現在のAPI制限によりレビュー本文・評価・返信はNEXUS内では扱わず、公式Google Business Profileへ誘導します。",
    },
  ];
  return {
    summary,
    insights,
    actions: [
      { action_id: "improve_website_cta", priority: totalActions ? "medium" : "high" },
      { action_id: "add_photos", priority: "medium" },
      { action_id: "update_hours", priority: "low" },
    ],
  };
}

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="gbp-modal-backdrop">
      <div className="gbp-modal">
        <div className="gbp-modal-head"><h3>{title}</h3><button onClick={onClose}><X size={18} /></button></div>
        {children}
      </div>
    </div>
  );
}

export const GoogleBusinessPage = () => {
  const [settings, setSettings] = useState<GbpSettings | null>(null);
  const [locationId, setLocationId] = useState<string | null>(null);
  const [dashboard, setDashboard] = useState<GbpDashboard | null>(null);
  const [businessInfo, setBusinessInfo] = useState<GbpBusinessInfo | null>(null);
  const [performance, setPerformance] = useState<GbpPerformance | null>(null);
  const [trendMetric, setTrendMetric] = useState<TrendMetric>("searches");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState<EditMode>(null);
  const [websiteUri, setWebsiteUri] = useState<string>("");
  const [hours, setHours] = useState<GbpHours[]>([]);
  // 2026-09-16: website 編集時のみ「公開情報更新」の Confirmation Dialog 経由にする。
  // - beforeUri:編集モーダルを開いた瞬間の値(=現在Google上に公開されている値)
  // - afterUri:編集中の値(=保存しようとしている新しい値)
  // - 非null の時だけ Confirmation Dialog を表示する。
  const [pendingConfirm, setPendingConfirm] = useState<
    { mode: "website"; beforeUri: string; afterUri: string } | null
  >(null);
  const [aiInsight, setAiInsight] = useState<InsightData | null>(null);
  const [aiLoading, setAiLoading] = useState(false);

  const locations = settings?.locations.filter((l) => l.isEnabled) || [];
  const currentLocationId = locationId || settings?.lastSelectedLocationId || settings?.locations.find((l) => l.isDefault)?.id || settings?.locations[0]?.id || null;
  const location = businessInfo?.location || dashboard?.location || performance?.location || settings?.currentLocation || locations[0] || null;
  const lastSyncedAt = dashboard?.lastSyncedAt || businessInfo?.lastSyncedAt || performance?.lastSyncedAt || settings?.connection?.lastSyncedAt;

  const loadShell = useCallback(async () => {
    const data = await googleBusinessApi.getLocations();
    setSettings(data);
    setLocationId(data.currentLocation?.id || data.lastSelectedLocationId || data.locations.find((l) => l.isDefault)?.id || data.locations[0]?.id || null);
  }, []);

  const loadPage = useCallback(async (refresh = false) => {
    if (!currentLocationId) return;
    setBusy(true);
    setError(null);
    try {
      const cacheKey = gbpPageCacheKey(currentLocationId, 30);
      if (!refresh) {
        const cached = readTimedCache<GbpPageCache>(cacheKey);
        if (cached) {
          setDashboard(cached.dashboard);
          setBusinessInfo(cached.businessInfo);
          setPerformance(cached.performance);
          return;
        }
      }
      const [dash, info, perf] = await Promise.all([
        googleBusinessApi.getDashboard(currentLocationId, refresh),
        googleBusinessApi.getBusinessInfo(currentLocationId, refresh),
        googleBusinessApi.getPerformance(currentLocationId, 30, refresh),
      ]);
      setDashboard(dash);
      setBusinessInfo(info);
      setPerformance(perf);
      writeTimedCache(cacheKey, { dashboard: dash, businessInfo: info, performance: perf });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [currentLocationId]);

  useEffect(() => { loadShell().catch((e) => setError(e instanceof ApiError ? e.message : String(e))); }, [loadShell]);
  useEffect(() => { loadPage(false); }, [loadPage]);

  async function changeLocation(id: string) {
    setLocationId(id);
    await googleBusinessApi.setCurrentLocation(id);
  }

  async function commitSave() {
    // 2026-09-16: 実際に API を叩く最終ステップ。
    // - website / hours 両方の body を組み立てて送信
    // - pendingConfirm を経由する場合は、websiteUri の値を引数経由で渡される
    if (!currentLocationId) return;
    setBusy(true);
    setError(null);
    try {
      const body = editMode === "website"
        ? { locationId: currentLocationId, websiteUri: pendingConfirm?.afterUri ?? websiteUri }
        : { locationId: currentLocationId, regularHours: hours };
      const data = await googleBusinessApi.patchBusinessInfo(body);
      setBusinessInfo(data);
      setEditMode(null);
      setPendingConfirm(null);
      window.sessionStorage.removeItem(gbpPageCacheKey(currentLocationId, 30));
      window.sessionStorage.removeItem(gbpInsightCacheKey(currentLocationId, 30));
      await loadPage(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  /**
   * 編集 Modal の「保存」ボタンから呼ばれる。
   * - website: 公開情報のため Confirmation Dialog を経由
   * - hours:  そのまま commitSave(必要なら同様に確認ダイアログを差し込めるが spec では要求なし)
   */
  function requestSave() {
    if (editMode === "website") {
      // 編集中の最新値を pendingConfirm に積む
      setPendingConfirm({
        mode: "website",
        beforeUri: location?.websiteUri || "",
        afterUri: websiteUri,
      });
      return;
    }
    void commitSave();
  }

  const metrics = useMemo(() => {
    const rows = performance?.daily || [];
    const half = Math.floor(rows.length / 2);
    return (Object.keys(TREND_META) as TrendMetric[]).map((metric) => {
      const value = performance?.totals[metric] ?? dashboard?.metrics30d[metric] ?? 0;
      const previous = rows.length >= 2 ? sumMetric(rows, metric, 0, half) : 0;
      const current = rows.length >= 2 ? sumMetric(rows, metric, half, rows.length) : value;
      return { metric, value, change: percentChange(current, previous), series: rows.map((row) => Number(row[metric] || 0)) };
    });
  }, [dashboard, performance]);

  const fallbackInsight = useMemo(() => buildInsight(dashboard, performance), [dashboard, performance]);
  const insight = aiInsight || fallbackInsight;
  const secondaryMetric = secondaryTrendMetric(trendMetric);
  const openGoogleReviews = () => window.open("https://business.google.com/reviews", "_blank", "noopener,noreferrer");

  useEffect(() => {
    if (!currentLocationId || !dashboard || !performance) {
      setAiInsight(null);
      return;
    }
    let cancelled = false;
    const period = performance.period || 30;
    const cached = readTimedCache<InsightData>(gbpInsightCacheKey(currentLocationId, period));
    if (cached) {
      setAiInsight(cached);
      setAiLoading(false);
      return () => { cancelled = true; };
    }
    setAiInsight(null);
    setAiLoading(true);
    googleBusinessApi.generateAiInsight({ locationId: currentLocationId, period })
      .then((result) => {
        if (!cancelled) {
          const normalized = normalizeAiInsight(result.insight);
          setAiInsight(normalized);
          if (normalized) writeTimedCache(gbpInsightCacheKey(currentLocationId, period), normalized);
        }
      })
      .catch(() => {
        if (!cancelled) setAiInsight(null);
      })
      .finally(() => { if (!cancelled) setAiLoading(false); });
    return () => { cancelled = true; };
  }, [currentLocationId, dashboard, performance]);

  return (
    <div className="gbp-page">
      {error && <div className="gbp-error">{error}</div>}
      {!currentLocationId || !location ? (
        <section className="gbp-panel gbp-empty">有効なGBP店舗がありません。設定画面で店舗を有効化してください。</section>
      ) : (
        <div className="gbp-dashboard-grid">
          <section className="gbp-card gbp-store-overview">
            <div className="gbp-store-photo"><Store size={44} /></div>
            <div className="gbp-store-copy">
              <span>店舗概要</span>
              <h2>{location.title}</h2>
              <p>{location.address || "住所はGoogleビジネスプロフィールで管理されています。"}</p>
              <div className="gbp-store-badges">
                <Badge color="green" dot>確認済み</Badge>
                <Badge color={location.openStatus === "OPEN" ? "green" : "yellow"} dot>{statusLabel(location.openStatus)}</Badge>
              </div>
            </div>
            <div className="gbp-store-actions">
              <StoreSwitcher locations={locations} currentLocationId={currentLocationId} onChange={changeLocation} />
              <Button
                variant="secondary"
                iconLeft={<RefreshCw size={15} />}
                onClick={() => {
                  if (currentLocationId) window.sessionStorage.removeItem(gbpInsightCacheKey(currentLocationId, 30));
                  void loadPage(true);
                }}
                disabled={busy}
              >
                {busy ? "更新中" : "更新"}
              </Button>
            </div>
            <div className="gbp-store-meta">
              <div><Clock size={15} /><span>{location.regularHours[0]?.status === "Closed" ? "本日は休業" : "営業時間同期済み"}</span></div>
              <div><Building2 size={15} /><span>{location.primaryCategory || "Googleのカテゴリ"}</span></div>
              <div><RefreshCw size={15} /><span>{fmtDate(lastSyncedAt)}</span></div>
            </div>
          </section>

          <section className="gbp-kpi-grid">
            {metrics.map((item) => <KpiCard key={item.metric} {...item} />)}
          </section>

          <section className="gbp-card gbp-trend-card">
            <div className="gbp-card-head">
              <div><h3>パフォーマンス推移</h3><p>直近30日間の動きを切り替えて確認します。</p></div>
              <div className="gbp-segmented">
                {(Object.keys(TREND_META) as TrendMetric[]).map((metric) => (
                  <button key={metric} className={trendMetric === metric ? "active" : ""} onClick={() => setTrendMetric(metric)}>{TREND_META[metric].label}</button>
                ))}
              </div>
            </div>
            <div className="gbp-trend-stack">
              <TrendChart data={performance?.daily || []} metric={trendMetric} />
              <TrendChart data={performance?.daily || []} metric={secondaryMetric} />
            </div>
          </section>

          <section className="gbp-card gbp-business-card">
            <div className="gbp-card-head"><div><h3>店舗情報</h3><p>編集できる項目とGoogle公式でのみ変更できる項目を分けて表示します。</p></div></div>
            <div className="gbp-business-grid">
              <div className="gbp-review-helper">
                <div className="gbp-review-icon"><Target size={18} /></div>
                <div>
                  <h3>Googleのクチコミ</h3>
                  <p>レビュー・評価・返信は公式画面で確認してください。</p>
                </div>
                <Button variant="secondary" iconLeft={<ExternalLink size={14} />} onClick={openGoogleReviews}>公式画面を開く</Button>
              </div>
              <div className="gbp-business-fields">
                <BusinessField icon={<Store size={16} />} label="店舗名" value={location.title} />
                <BusinessField icon={<Phone size={16} />} label="電話番号" value={location.phone} />
                <BusinessField
                  icon={<Globe size={16} />}
                  label="ウェブサイト"
                  value={location.websiteUri}
                  editable
                  external
                  onEdit={() => {
                    setWebsiteUri(location.websiteUri || "");
                    setEditMode("website");
                  }}
                />
                <BusinessField icon={<MapPin size={16} />} label="住所" value={location.address} />
                <BusinessField icon={<Building2 size={16} />} label="メインカテゴリ" value={location.primaryCategory} />
                <BusinessField icon={<CheckCircle2 size={16} />} label="営業ステータス" value={statusLabel(location.openStatus)} />
              </div>
              <div className="gbp-hours-block">
                <div><Clock size={16} /><strong>営業時間</strong><button onClick={() => { setHours(location.regularHours || []); setEditMode("hours"); }}><Edit3 size={14} /> 編集</button></div>
                {(location.regularHours || []).slice(0, 7).map((hour) => <p key={hour.day}><span>{hour.day}</span>{hour.status === "Closed" ? "休業" : `${hour.open} - ${hour.close}`}</p>)}
              </div>
            </div>
          </section>

          <section className="gbp-card gbp-ai-card-new">
            <div className="gbp-card-head"><div><h3><Sparkles size={18} /> AIインサイト</h3><p>GBPの表示可能な数値だけで要点をまとめます。</p></div></div>
            {aiLoading ? (
              <div className="ai-insight-generating" role="status" aria-live="polite">
                <span className="ai-insight-generating-orb" />
                <span>AIがデータを整理しています<span className="ai-insight-generating-dots" /></span>
              </div>
            ) : <AiInsightPanel data={insight} />}
          </section>
        </div>
      )}

      {editMode && (
        <Modal
          title={editMode === "website" ? "ウェブサイト URL を編集" : "営業時間を編集"}
          onClose={() => setEditMode(null)}
        >
          <div className="gbp-public-warning">
            保存すると公開中のGoogleビジネスプロフィール情報が更新されます。
          </div>
          {editMode === "website" ? (
            <div className="gbp-form-grid">
              <label>
                ウェブサイト URL
                <input
                  value={websiteUri}
                  onChange={(e) => setWebsiteUri(e.target.value)}
                  placeholder="例: https://example.com"
                />
              </label>
            </div>
          ) : (
            <div className="gbp-hours-editor">
              {hours.map((h, i) => (
                <div key={h.day} className="gbp-hour-edit-row">
                  <strong>{h.day}</strong>
                  <input value={h.open} onChange={(e) => setHours(hours.map((x, idx) => idx === i ? { ...x, open: e.target.value } : x))} />
                  <input value={h.close} onChange={(e) => setHours(hours.map((x, idx) => idx === i ? { ...x, close: e.target.value } : x))} />
                  <select value={h.status} onChange={(e) => setHours(hours.map((x, idx) => idx === i ? { ...x, status: e.target.value } : x))}><option value="Open">営業</option><option value="Closed">休業</option></select>
                </div>
              ))}
            </div>
          )}
          <div className="gbp-modal-actions">
            <Button variant="secondary" onClick={() => setEditMode(null)}>キャンセル</Button>
            <Button onClick={requestSave} disabled={busy}>
              {editMode === "website" ? "保存" : "公開情報を保存"}
            </Button>
          </div>
        </Modal>
      )}

      {/* 2026-09-16: Confirmation Dialog (website 編集のみ)。
          公開情報の最終確認。「更新する」を押した瞬間だけ commitSave が走る。
          キャンセル時は編集モーダルに戻る(websiteUri の編集状態は保持)。 */}
      {pendingConfirm && (
        <Modal title="WebサイトURLを更新しますか？" onClose={() => setPendingConfirm(null)}>
          <div className="gbp-confirm-body">
            <p className="gbp-confirm-intro">
              この変更は <strong>Google Business Profile の公開情報</strong> へ反映されます。<br />
              変更内容をご確認のうえ、問題なければ「更新する」を押してください。
            </p>
            <div className="gbp-confirm-diff">
              <div className="gbp-confirm-row">
                <span className="gbp-confirm-label">現在</span>
                <code className="gbp-confirm-value">{pendingConfirm.beforeUri || "(未設定)"}</code>
              </div>
              <div className="gbp-confirm-arrow" aria-hidden>↓</div>
              <div className="gbp-confirm-row">
                <span className="gbp-confirm-label">変更後</span>
                <code className="gbp-confirm-value gbp-confirm-value--new">{pendingConfirm.afterUri || "(未設定)"}</code>
              </div>
            </div>
          </div>
          <div className="gbp-modal-actions">
            <Button variant="secondary" onClick={() => setPendingConfirm(null)}>キャンセル</Button>
            <Button onClick={commitSave} disabled={busy}>{busy ? "更新中" : "更新する"}</Button>
          </div>
        </Modal>
      )}
    </div>
  );
};
